import type { ReactNode } from "react";
import { usePortalIdentity } from "../auth/usePortalIdentity";
import { CLIENT_TIER_LABEL, useCurrentManagement, type CurrentManagement } from "../data/managementQueries";
import { Badge, Quiet } from "../ui/Badge";
import { BoxState } from "../ui/BoxState";
import { Callout } from "../ui/Callout";
import { Panel } from "../ui/Panel";
import "./clientSettings/settings.css";

/**
 * Förvaltning: the client's own side of plan.md section 5.12.
 *
 * The loyalty discount of the removed section 5.13 was taken off this screen on 2026-09-22, because
 * the company stopped selling it on 2026-09-08. The database objects remain until a later round
 * decides their fate.
 *
 * **What state the management is in, and nothing that changes it, since 2026-10-07.** Until that
 * day the authorised contact ended management here, behind a step-up, withdrew the cancellation and
 * answered the question about their attachments. Mattej decided that a client who wants to end
 * writes to Petrovic by mail or in the chat, and Petrovic records it on the client's Avslut tab
 * (`app/routes/adminClient/EndTab.tsx`). The rejected alternative is the button that stood here: a
 * client could press it by mistake, or without having talked to Petrovic first (plan.md section
 * 5.12). The four gateways it called were dropped by `20261007100001`, so this screen reads the
 * state and says in one line how to change it.
 *
 * **Nothing on this screen is calculated.** The confirmed end date comes from the database. 5.12 puts
 * it in `private.management_confirmed_end_on` because it is the end of the invoiced month rather than
 * thirty days from today. A second implementation of that sum in a browser would be right until the
 * month it was not. The portal creates no offers and calculates no amounts.
 *
 * **Facts in a box since the second build round of 2026-09-24** (`prototype/kund/pages.js`, the
 * `forvaltning` section). Läge and Sedan as a list of facts. The lead under the heading went under
 * design.md section 3's third rule; the notice about reading until a date did not, because it is the
 * only place an offboarding client is told when login stops.
 */

const dateFormat = new Intl.DateTimeFormat("sv-SE", { dateStyle: "long" });

/**
 * A `date` column, formatted in local time from its own parts.
 *
 * `new Date("2028-03-14")` is parsed as UTC midnight, so every timezone behind UTC renders it as the
 * thirteenth. These are the dates a client reads and acts on, and an end date that is one day early
 * on a screen in Reykjavik is the kind of defect nobody finds until somebody argues about it.
 */
function formatDate(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!parts) {
    return null;
  }

  const parsed = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  return Number.isNaN(parsed.getTime()) ? null : dateFormat.format(parsed);
}

/* -------------------------------------------------------------------- state */

/**
 * Läge as one word, and the tone it gets.
 *
 * design.md section 3's second rule: what is normal is a grey word, and only what asks something of
 * the client gets colour. An active management is the normal case and is the quiet word "Aktiv"; one
 * that has not started yet is information and nothing to act on; everything else changes what the
 * client can do and carries the warning tone. The word always carries the meaning.
 */
interface StatePresentation {
  word: string;
  tone: "quiet" | "info" | "warn";
  note: string | null;
}

function stateFor(management: CurrentManagement): StatePresentation {
  const scheduled = formatDate(management.scheduled_end_on);

  switch (management.status) {
    case "pending_activation":
      return {
        word: "Har inte börjat än",
        tone: "info",
        note: "Den börjar den dag det vi levererat är publicerat eller taget i bruk."
      };
    case "active":
      return { word: "Aktiv", tone: "quiet", note: null };
    // No note of its own: what a pause means for the client's access is the read notice further
    // down, driven by `portal_access_state`.
    case "paused":
      return { word: "Pausad", tone: "warn", note: null };
    // plan.md 5.12 point 7 fixes this wording. It is the status line a client sees from the moment
    // the cancellation is registered until the end date arrives, and it is where the end date is
    // shown, once (design.md section 3, rule 1).
    case "ending":
      return {
        word: scheduled ? `Förvaltning avslutas ${scheduled}` : "Förvaltningen är uppsagd",
        tone: "warn",
        note: scheduled
          ? "Fram till det datumet fungerar allt som vanligt."
          : "Vi kan inte visa slutdatumet just nu. Skriv i chatten så svarar vi."
      };
    case "read_only":
    case "ended":
      return { word: "Avslutad", tone: "warn", note: null };
    default:
      return {
        word: "Okänt läge",
        tone: "warn",
        note: "Vi kan inte säga vad förvaltningen har för läge just nu."
      };
  }
}

/**
 * The one state a client most needs told exactly, so it is told exactly.
 *
 * plan.md 5.12: at the end date client access becomes read-only for 30 days, and after 30 days
 * login is disabled. Both halves are here, because "read-only" without "and then you cannot log in"
 * is the half that lets somebody plan to come back later for their documents. It is the offboarding
 * client's only statement of when login stops, which is why it stayed through the round of
 * 2026-09-24 that took every other paragraph off this box.
 */
function ReadOnlyNotice({ management }: { management: CurrentManagement }) {
  const endedOn = formatDate(management.actual_end_on);
  const until = formatDate(management.read_only_until);

  return (
    <Callout tone="warn" testId="read-only-notice">
      {endedOn ? `Förvaltningen avslutades ${endedOn}. ` : ""}
      {until
        ? `Ni kan läsa i portalen till och med ${until}. Efter det datumet går det inte att logga in längre.`
        : "Ni kan läsa i portalen under en period och därefter går det inte att logga in längre. Vi kan inte visa slutdatumet just nu."}{" "}
      Vill ni ha ut ert material innan dess, säg till i chatten så skickar vi det.
    </Callout>
  );
}

/* ------------------------------------------------------- how it is changed */

/**
 * The one line that says how the management is ended or kept, since 2026-10-07.
 *
 * In writing, by mail or in the chat, and only the authorised contact's word counts, which plan.md
 * section 4 has always said: a member "cannot end management or change access". A member is told
 * whose decision it is rather than how to make it.
 *
 * An ending Petrovic made is not the client's to withdraw, so that case says where to go instead of
 * offering the withdrawal. `end_initiator` is `client` for a cancellation Petrovic recorded on the
 * client's behalf, because the client is still the one who ended it.
 */
function HowItChanges({
  management,
  isAuthorisedContact
}: {
  management: CurrentManagement;
  isAuthorisedContact: boolean;
}) {
  if (management.status === "active") {
    return isAuthorisedContact ? (
      <span className="quiet k-set-foot-line" data-testid="management-end-how">
        Vill ni säga upp förvaltningen? Skriv till oss i chatten eller per mejl, så bekräftar vi
        uppsägningen och slutdatumet.
      </span>
    ) : (
      <span className="quiet k-set-foot-line" data-testid="management-role-note">
        Att säga upp förvaltningen gör den hos er som står som avtalsansvarig kontaktperson, i chatten
        eller per mejl.
      </span>
    );
  }

  if (management.status === "ending") {
    if (management.end_initiator !== "client") {
      return (
        <span className="quiet k-set-foot-line" data-testid="withdraw-not-clients">
          Uppsägningen kommer från Petrovic Systems. Skriv i chatten om något ska ändras.
        </span>
      );
    }

    return isAuthorisedContact ? (
      <span className="quiet k-set-foot-line" data-testid="management-withdraw-how">
        Vill ni ångra uppsägningen? Skriv till oss i chatten eller per mejl före slutdatumet.
      </span>
    ) : (
      <span className="quiet k-set-foot-line" data-testid="management-role-note">
        Att ångra uppsägningen gör den hos er som står som avtalsansvarig kontaktperson.
      </span>
    );
  }

  return null;
}

/* ------------------------------------------------------------------- panel */

export function ManagementPanel() {
  const identity = usePortalIdentity();
  const tenantId = identity.data?.tenantId ?? null;
  const management = useCurrentManagement(tenantId);

  const frame = (children: ReactNode) => (
    <Panel title="Förvaltning" titleId="management-title" flush>
      {children}
    </Panel>
  );

  if (identity.isLoading || management.isLoading) {
    return frame(<BoxState kind="loading" compact title="Hämtar er förvaltning" />);
  }

  /*
    Checked before the tenant. `usePortalIdentity` answers with no tenant when its own read failed,
    and the refusal below would then be shown for a network fault.
  */
  if (identity.isError) {
    return frame(
      <BoxState
        kind="error"
        compact
        title="Vi kunde inte läsa vilket kundkonto det gäller"
        body="Ingenting har ändrats. Försök igen."
        onRetry={() => void identity.refetch()}
      />
    );
  }

  if (!tenantId) {
    return frame(
      <BoxState
        kind="denied"
        compact
        title="Den här vyn är inte din"
        body="Kontot hör inte till någon kund i portalen."
      />
    );
  }

  if (management.isError) {
    return frame(
      <BoxState
        kind="error"
        compact
        title="Förvaltningen gick inte att hämta"
        body="Den här delen gick inte att hämta just nu. Resten av sidan stämmer. Försök igen."
        onRetry={() => void management.refetch()}
      />
    );
  }

  const current = management.data ?? null;

  /*
    The view drops a period that has fully ended, so no row is a real answer and not a failed read.
  */
  if (!current) {
    return frame(
      <BoxState
        kind="empty"
        compact
        title="Ni har ingen förvaltning i portalen"
        body="Det finns ingen löpande förvaltning på det här kundkontot. Skriv i chatten om ni vill ha en."
      />
    );
  }

  const state = stateFor(current);
  const started = formatDate(current.started_on);
  const isAuthorisedContact = identity.data?.role === "client_authorised_contact";

  const foot = <HowItChanges management={current} isAuthorisedContact={isAuthorisedContact} />;

  return (
    <Panel title="Förvaltning" titleId="management-title" foot={foot} testId="management-panel">
      <div className="k-set-lines">
        <dl className="kv">
          <div>
            <dt>Läge</dt>
            <dd data-testid="management-state">
              {state.tone === "quiet" ? (
                <Quiet>{state.word}</Quiet>
              ) : (
                <Badge tone={state.tone}>{state.word}</Badge>
              )}
            </dd>
          </div>
          {started && current.status !== "pending_activation" ? (
            <div>
              <dt>Sedan</dt>
              <dd>{started}</dd>
            </div>
          ) : null}
          {/*
            Build round A1, 2026-10-08: which of the two the client has, in the product's full name.
            A grey fact, because either is a normal state. Here and not on Översikt, which draws no
            management box while management is normal (design.md section 3, rule 2).
          */}
          {current.tier ? (
            <div data-testid="management-tier">
              <dt>Nivå</dt>
              <dd>{CLIENT_TIER_LABEL[current.tier] ?? current.tier}</dd>
            </div>
          ) : null}
        </dl>

        {state.note ? <p className="line-hint">{state.note}</p> : null}

        {/*
          plan.md section 5.12 step 4, asked for on this screen since the sheet that asked it is
          gone. Petrovic records the answer in admin; the mail confirming the cancellation asks too
          and names the date. Without an answer the bilagor are deleted with the rest, twelve
          months after the end date, which the sentence says because the block is a deadline and
          not a hold (`tests/processor-block-claim.test.ts`).
        */}
        {current.status === "ending" &&
        isAuthorisedContact &&
        current.processor_end_instruction === "not_yet_given" ? (
          <p className="line-hint" data-testid="instruction-ask">
            Säg till i chatten eller per mejl om bilagorna ni har laddat upp i ärenden ska lämnas
            tillbaka till er eller raderas. Vi väljer inte åt er. Kommer inget besked raderas de
            tolv månader efter slutdatumet, tillsammans med resten av ert material i portalen.
          </p>
        ) : null}

        {/*
          Driven by `portal_access_state` rather than by the status beside it. The view derives one
          from the other so they cannot disagree today, and the rule `usePortalIdentity` sets is
          still the right one: the interface follows the server's own word on whether writing is
          open instead of deriving it a second time.
        */}
        {current.portal_access_state === "read" ? (
          current.status === "read_only" ? (
            <ReadOnlyNotice management={current} />
          ) : (
            <Callout tone="warn" testId="read-access-notice">
              Ni kan läsa i portalen men inte skriva just nu. Ingenting av det ni har lagt upp
              försvinner.
            </Callout>
          )
        ) : null}
      </div>
    </Panel>
  );
}
